// @ts-check

import React from 'react';
import { func, string } from 'prop-types';
import useProfileBadge from '../hooks/useProfileBadge';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

/**
 * @component ProfileBadge
 * @param {Object} props
 * @param {string} props.userImg  blob:http://localhost:8000/1685e6a0-8f33-40e0-bb00-05f0845b034a
 * @param {(name: string, payload: any) => void} props.onHandle
 * @returns {JSX.Element}
 */
function ProfileBadge({ userImg, onHandle }) {
	const { state, handler } = useProfileBadge(onHandle);

	return (
		<>
			<Badge
				sx={{ '& .MuiBadge-badge': { top: '5rem', right: '1.75rem' } }}
				overlap="circular"
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				badgeContent={
					<div
						style={{
							border: 'double 1px var(--cl-text-disabled)',
							borderRadius: '50%',
							backgroundColor: 'var(--cl-paper-bg-light)',
						}}
					>
						<Tooltip title="Add Image">
							<IconButton onClick={() => handler.onOpenImg(true)}>
								<AddAPhotoIcon sx={{ width: '2rem', height: '2rem' }} />
							</IconButton>
						</Tooltip>
					</div>
				}
			>
				<Avatar sx={{ width: '8rem', height: '8rem' }} alt="User" src={userImg} />
			</Badge>
			<Dialog open={state.openImg} fullWidth maxWidth="sm">
				<DialogTitle
					sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}
				>
					<Typography component="span" variant="h5" color="primary">
						Upload a image
					</Typography>
					<Tooltip title="Close">
						<IconButton color="primary" onClick={() => handler.onOpenImg(false)}>
							<CloseIcon />
						</IconButton>
					</Tooltip>
				</DialogTitle>
				<DialogContent>
					<Paper
						sx={{
							display: 'flex',
							flexDirection: 'column',
							textAlign: 'center',
							alignItems: 'center',
							padding: '1rem',
						}}
						variant="outlined"
					>
						<Typography
							sx={{ color: 'var(--cl-grey-400)' }}
							component="span"
							variant="body1"
							gutterBottom
						>
							Click here to upload a image.
						</Typography>
						<input
							id="upload-button"
							type="file"
							accept="image/png, image/jpeg, image/jpg"
							hidden
							onChange={handler.onHandleImg}
						/>
						<label
							style={{ cursor: 'pointer', padding: '0.5rem 0rem', width: '100%' }}
							htmlFor="upload-button"
						>
							<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
								{state.preview ? (
									<Avatar
										sx={{
											width: '10rem',
											height: '10rem',
											pointerEvents: 'none',
											cursor: 'default',
										}}
										alt="User Image"
										src={state.preview}
									/>
								) : (
									<CloudUploadIcon
										sx={{
											width: '10rem',
											height: '10rem',
											pointerEvents: 'none',
											cursor: 'default',
										}}
										color="primary"
									/>
								)}
							</div>
						</label>
						<Typography
							style={{ color: 'var(--cl-grey-700)' }}
							component="span"
							variant="subtitle1"
							gutterBottom
						>
							Supported Files
						</Typography>
						<Typography
							style={{ color: 'var(--cl-grey-400)' }}
							component="span"
							variant="caption"
							gutterBottom
						>
							JPG, JPEG, PNG
						</Typography>
					</Paper>
				</DialogContent>
				<DialogActions
					sx={{
						display: 'flex',
						flexDirection: 'row',
						gap: '1rem',
						paddingLeft: '1.5rem',
						paddingRight: '1.5rem',
						paddingBottom: '1.5rem',
					}}
				>
					<Button
						name="reset"
						variant="outlined"
						fullWidth
						onClick={(e) => handler.onHandleButton(e.currentTarget.name)}
					>
						RESET
					</Button>
					<Button
						name="chose"
						variant="contained"
						fullWidth
						disabled={state.canNotSave}
						onClick={(e) => handler.onHandleButton(e.currentTarget.name)}
					>
						CHOSE
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}

ProfileBadge.propTypes = { userImg: string, onHandle: func };

export default ProfileBadge;
